import { useMemo } from 'react';
import {
  useAuthSession,
  useStorePromotionalAds,
  WebsiteTemplateServiceError,
  type StorePromotionalAdStoreContext,
  type StorePromotionalAdWithPreview,
} from '@dukanti/shared';
import { useCurrentStore } from '@/hooks/use-current-store';
import { supabase } from '@/lib/supabase';
import { usePromotionalAdsEditorState } from './use-promotional-ads-editor-state';

/**
 * Epic #228 P7. Standalone Ads authoring is legacy-only: a store whose site is
 * a fixed template edits its promotion placements on the storefront itself.
 *
 * - `loading`: not settled yet. Render no Ads tab and no authoring UI.
 * - `authoring`: legacy store (or no website row, which the SQL reads as
 *   legacy). The Ads pages behave exactly as before P7.
 * - `readOnly`: not authoring, but the store still holds ads rows. The list is
 *   shown without any mutation control, with a notice.
 * - `hidden`: not authoring and no ads rows (or they could not be read). No
 *   Ads tab; the Ads routes send the merchant to Campaigns.
 * - `error`: the editor state failed to load (network, unknown) and nothing
 *   is cached, so whether the store is legacy is unknown. The Ads page shows a
 *   neutral load error with Retry; no notice claims the store uses a template.
 */
export type PromotionalAdsAccessState = 'loading' | 'authoring' | 'readOnly' | 'hidden' | 'error';

/**
 * Which notice explains a `readOnly` list. `staff` when the editor state was
 * refused (`permission_denied`: the viewer is not a store admin, so whether
 * the store is legacy is unknown); `legacy` when the editor state says the
 * store is no longer legacy.
 */
export type PromotionalAdsAccessNotice = 'legacy' | 'staff';

export interface PromotionalAdsAccess {
  readonly state: PromotionalAdsAccessState;
  /** Null while the ads rows are not needed (`authoring`, `error`) or not settled. */
  readonly hasRows: boolean | null;
  /** Set exactly when `state` is `readOnly`. */
  readonly notice: PromotionalAdsAccessNotice | null;
  /** The retained ads rows the gate read; non-empty exactly when `readOnly`. */
  readonly ads: readonly StorePromotionalAdWithPreview[];
  /** Reloads the editor state (the `error` state's Retry). */
  readonly retry: () => void;
}

const NO_ADS: readonly StorePromotionalAdWithPreview[] = [];

function isPermissionDenied(error: unknown): boolean {
  return error instanceof WebsiteTemplateServiceError && error.code === 'permission_denied';
}

/**
 * The Marketing gate. UI convenience only: the ad RPCs and RLS stay the
 * server boundary. Never throws. It reads queries only (no mutation hook).
 *
 * Call it once per route, at the route level, and pass the result down
 * (`MarketingTabs` takes it as a prop). A route swaps subtrees between its
 * states; an owner inside a swapped subtree would remount, re-hydrate its
 * session and move its query key on every swap (BUG-P7-1).
 */
export function usePromotionalAdsAccess(): PromotionalAdsAccess {
  const authSession = useAuthSession(supabase);
  const store = useCurrentStore(authSession);
  const storeId = store.storeId;
  const storeSlug = store.storeSlug ?? null;
  const { query: editor, retry } = usePromotionalAdsEditorState(supabase, storeId);

  const hasData = editor.data !== undefined;
  const editorSettled = editor.isError || hasData;
  // Cached data wins over a background refetch error (same rule as
  // `use-storefront-editor-data.ts`): a legacy store keeps authoring on a
  // flaky refetch.
  const isLegacy = editor.data?.mode === 'legacy';
  const refused = !hasData && editor.isError && isPermissionDenied(editor.error);
  const loadFailed = !hasData && editor.isError && !refused;
  const needsRows = storeId !== null && editorSettled && !isLegacy && !loadFailed;

  const rowsStore = useMemo<StorePromotionalAdStoreContext | null>(
    () => (needsRows && storeId && storeSlug ? { storeId, storeSlug } : null),
    [needsRows, storeId, storeSlug],
  );
  const rows = useStorePromotionalAds(supabase, rowsStore);

  const result = (
    state: PromotionalAdsAccessState,
    hasRows: boolean | null = null,
    notice: PromotionalAdsAccessNotice | null = null,
  ): PromotionalAdsAccess => ({
    state,
    hasRows,
    notice,
    ads: state === 'readOnly' ? (rows.data ?? NO_ADS) : NO_ADS,
    retry,
  });

  if (store.isLoading) return result('loading');
  // No store: nothing to gate. The Ads pages keep their own no-store error.
  if (storeId === null) return result('authoring');
  if (!editorSettled) return result('loading');
  if (isLegacy) return result('authoring');
  if (loadFailed) return result('error');

  // Cached rows win over a failed background refetch, like the editor state:
  // only a first-load failure (no data) hides the Ads page.
  if (rowsStore === null || (rows.isError && rows.data === undefined)) {
    return result('hidden', false);
  }
  if (rows.data === undefined) return result('loading');
  if (rows.data.length === 0) return result('hidden', false);
  return result('readOnly', true, refused ? 'staff' : 'legacy');
}
